import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Users, Sparkles } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function RelationshipCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'AI Relationship Coach - Navigate Dating & Relationships | VZMO';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Get AI-powered relationship coaching for dating challenges, communication issues, and relationship decisions. Private, judgment-free guidance 24/7. Start free.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/coach/relationship-coach');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/coach/relationship-coach';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('relationship-coach-page');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            AI Relationship Coach
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Navigate dating challenges, improve communication, and work through relationship decisions
            with private AI coaching. Available 24/7 when you need it most.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            What We Help You With
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <MessageCircle className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Communication Challenges</h3>
                <p className="text-slate-600">
                  Work through difficult conversations, understand different communication styles,
                  and express your needs effectively.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Heart className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Dating Decisions</h3>
                <p className="text-slate-600">
                  Clarify what you want, recognize red flags, and make choices aligned with
                  your values and long-term goals.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Users className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Relationship Patterns</h3>
                <p className="text-slate-600">
                  Identify recurring patterns, understand your attachment style, and break
                  unhealthy cycles.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Sparkles className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Personal Growth</h3>
                <p className="text-slate-600">
                  Build self-awareness, set boundaries, and become the partner you want to be
                  in relationships.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Dating Issues</h3>
            <ul className="space-y-2 text-slate-600 text-sm">
              <li>Online dating fatigue</li>
              <li>First date anxiety</li>
              <li>When to become exclusive</li>
              <li>Recognizing red flags</li>
              <li>Moving on after breakup</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Relationship Challenges</h3>
            <ul className="space-y-2 text-slate-600 text-sm">
              <li>Communication breakdowns</li>
              <li>Trust issues</li>
              <li>Long-distance struggles</li>
              <li>Work-life balance</li>
              <li>Future planning conflicts</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Personal Growth</h3>
            <ul className="space-y-2 text-slate-600 text-sm">
              <li>Setting boundaries</li>
              <li>Building self-esteem</li>
              <li>Understanding patterns</li>
              <li>Emotional regulation</li>
              <li>Self-love practices</li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <Heart className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Get Relationship Clarity</h2>
          <p className="text-lg mb-6 opacity-90">
            Start your free 7-day trial. Private coaching available 24/7.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Why AI Relationship Coaching?</h2>
          <div className="space-y-4 text-slate-600">
            <p>
              Relationship coaches typically charge $100-200 per session. VZMO gives you unlimited access
              to thoughtful, judgment-free coaching whenever you need it - whether that's 2am after a difficult
              conversation or during your lunch break.
            </p>
            <p>
              Because conversations are private and encrypted, many users find it easier to be completely honest
              about their feelings, fears, and relationship dynamics than they would be with friends or even
              human coaches.
            </p>
            <p>
              <strong>Note:</strong> VZMO is not a replacement for couples therapy or mental health treatment.
              For relationship crises or mental health concerns, please consult licensed professionals.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
